import PayrollService from '@/api/PayrollService'

export const transformPaySlipLatestCode = (input = {}) => {
  return {
    'current': input.current,
    'next': input.next,
  }
}

export const transformPaySlip = (input = {}) => {
  return {
    'prlId': input.prl_id,
    'prlCode': input.prl_code,
    'status': input.status,
    'dateStart': input.date_start,
    'dateEnd': input.date_end,
    'note': input.note,
    'process': {
      'status': input.process && input.process.status,
      'message': input.process && input.process.message,
      'debit': input.process && input.process.debit,
    },
    'net': input.net,
    'appliedCount': input.applied_count,
    'createdAt': input.created_at,
    'employees': !input.employees ? [] : input.employees.map((emp) => {
      return {
        'prlId': emp.prl_id,
        'empId': emp.emp_id,
        'name': emp.emp_name,
        'wageAmount': emp.wage_amount,
        'conditions': emp.conditions.map((cond) => {
          return {
            'id': cond._id,
            'conditionId': cond.condition_id,
            'name': cond.name,
            'amount': cond.amount,
            'isIncrement': cond.is_increment,
            'isEditable': cond.is_editable,
          }
        }),
      }
    }),
    'payments': !input.payments ? [] : input.payments.map((pe) => {
      return {
        'payment': {
          'type': pe.payment.type,
          'amount': pe.payment.amount,
        },
        'ref': {
          'poId': pe.ref.po_id,
          'exId': pe.ref.ex_id,
          'inId': pe.ref.in_id,
          'prlId': pe.ref.prl_id,
        },
        'peId': pe.pe_id,
        'peCode': pe.pe_code,
        'status': pe.status,
        'type': pe.type,
      }
    }),
  }
}

export const transformPaySlips = (input = []) => {
  return input.map((item) => transformPaySlip(item))
}

export const transformEmployees = (input = []) => {
  return input.map((item) => {
    return {
      'empId': item.emp_id,
      'name': item.name,
    }
  })
}

export const transformEmployeeGroup = (input = {}) => {
  return {
    'empGroupId': input.emp_group_id,
    'name': input.name,
    'empIds': input.emp_ids || [],
    'appliedCount': input.applied_count,
    'createdAt': input.created_at,
    'employees': transformEmployees(input.employees || []),
  }
}

export const transformEmployeeGroups = (input = []) => {
  return input.map((item) => transformEmployeeGroup(item))
}

export const transformCondition = (input = {}) => {
  return {
    'conditionId': input.condition_id,
    'type': input.type,
    'name': input.name,
    'isIncrement': input.is_increment,
    'condition': (() => {
      if (!input.condition) {
        return null
      }
      return {
        'variable': input.condition.variable,
        'operator': input.condition.operator,
        'value': input.condition.value,
        'iftrue': input.condition.if_true,
        'iffalse': input.condition.if_false,
      }
    })(),
    'autoApplyNewEmp': input.auto_apply_new_emp,
    'createdAt': input.created_at,
    'deletedAt': input.deleted_at,
  }
}

export const transformConditions = (input = []) => {
  return input.map((item) => transformCondition(item))
}

export default {
  namespaced: true,

  state: {
    paySlipLatestCode: transformPaySlipLatestCode({}),
    paySlipList: transformPaySlips([]),
    paySlipSingle: transformPaySlip({}),
    employeeGroupList: transformEmployeeGroups([]),
    employeeGroupSingle: transformEmployeeGroup({}),
    conditionList: transformConditions([]),
    conditionSingle: transformCondition({}),
  },

  mutations: {
    setPaySlipLatestCode (state, data) {
      state.paySlipLatestCode = transformPaySlipLatestCode(data)
    },
    setPaySlipList (state, data) {
      state.paySlipList = transformPaySlips(data)
    },
    setPaySlipSingle (state, data) {
      state.paySlipSingle = transformPaySlip(data)
    },
    setEmployeeGroupList (state, data) {
      state.employeeGroupList = transformEmployeeGroups(data)
    },
    setEmployeeGroupSingle (state, data) {
      state.employeeGroupSingle = transformEmployeeGroup(data)
    },
    setConditionList (state, data) {
      state.conditionList = transformConditions(data)
    },
    setConditionSingle (state, data) {
      state.conditionSingle = transformCondition(data)
    },
  },

  actions: {
    async getPaySlipList ({ commit }, params = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getPaySlipList') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getPaySlipList')

        const response = await PayrollService.getPaySlipList(params)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getPaySlipList')

        result = response.data

        if (result.success) {
          commit('setPaySlipList', result.data)
          this.dispatch('setPagination', result.meta)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getPaySlipSingle ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getPaySlipSingle') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getPaySlipSingle')

        const response = await PayrollService.getPaySlipSingle(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getPaySlipSingle')

        result = response.data

        if (result.success) {
          commit('setPaySlipSingle', result.data)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getPaySlipLatestCode ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getPaySlipLatestCode') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getPaySlipLatestCode')

        const response = await PayrollService.getPaySlipLatestCode(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getPaySlipLatestCode')

        result = response.data

        if (result.success) {
          commit('setPaySlipLatestCode', result.data)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async createPaySlip ({ commit }, data = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('createPaySlip') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'createPaySlip')

        const response = await PayrollService.createPaySlip(data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'createPaySlip')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async updatePaySlip ({ commit }, { id, data }) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('updatePaySlip') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'updatePaySlip')

        const response = await PayrollService.updatePaySlip(id, data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'updatePaySlip')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async updatePaySlipStatus ({ commit }, { id, data }) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('updatePaySlipStatus') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'updatePaySlipStatus')

        const response = await PayrollService.updatePaySlipStatus(id, data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'updatePaySlipStatus')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async deletePaySlip ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('deletePaySlip') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'deletePaySlip')

        const response = await PayrollService.deletePaySlip(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'deletePaySlip')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getEmployeeGroupList ({ commit }, params = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getEmployeeGroupList') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getEmployeeGroupList')

        const response = await PayrollService.getEmployeeGroupList(params)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getEmployeeGroupList')

        result = response.data

        if (result.success) {
          commit('setEmployeeGroupList', result.data)
          this.dispatch('setPagination', result.meta)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getEmployeeGroupSingle ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getEmployeeGroupSingle') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getEmployeeGroupSingle')

        const response = await PayrollService.getEmployeeGroupSingle(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getEmployeeGroupSingle')

        result = response.data

        if (result.success) {
          commit('setEmployeeGroupSingle', result.data)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async createEmployeeGroup ({ commit }, data = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('createEmployeeGroup') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'createEmployeeGroup')

        const response = await PayrollService.createEmployeeGroup(data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'createEmployeeGroup')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async updateEmployeeGroup ({ commit }, { id, data }) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('updateEmployeeGroup') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'updateEmployeeGroup')

        const response = await PayrollService.updateEmployeeGroup(id, data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'updateEmployeeGroup')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async updateEmployeeGroupAssign ({ commit }, { id, data }) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('updateEmployeeGroupAssign') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'updateEmployeeGroupAssign')

        const response = await PayrollService.updateEmployeeGroupAssign(id, data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'updateEmployeeGroupAssign')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async deleteEmployeeGroup ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('deleteEmployeeGroup') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'deleteEmployeeGroup')

        const response = await PayrollService.deleteEmployeeGroup(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'deleteEmployeeGroup')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getConditionList ({ commit }, params = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getConditionList') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getConditionList')

        const response = await PayrollService.getConditionList(params)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getConditionList')

        result = response.data

        if (result.success) {
          commit('setConditionList', result.data)
          this.dispatch('setPagination', result.meta)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async getConditionSingle ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('getConditionSingle') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'getConditionSingle')

        const response = await PayrollService.getConditionSingle(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'getConditionSingle')

        result = response.data

        if (result.success) {
          commit('setConditionSingle', result.data)
        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async createCondition ({ commit }, data = {}) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('createCondition') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'createCondition')

        const response = await PayrollService.createCondition(data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'createCondition')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async updateCondition ({ commit }, { id, data }) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('updateCondition') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'updateCondition')

        const response = await PayrollService.updateCondition(id, data)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'updateCondition')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
    async deleteCondition ({ commit }, id) {
      var result

      // check this api is still loading or not
      if (this.state.app.loadinglist.indexOf('deleteCondition') >= 0) return

      try {
        // start loading
        this.dispatch('Loading', true)
        // clear error
        this.dispatch('Error', null)
        // add loadinglist
        this.dispatch('addLoadinglist', 'deleteCondition')

        const response = await PayrollService.deleteCondition(id)

        // stop loading
        this.dispatch('Loading', false)
        // remove loadinglist
        this.dispatch('removeLoadinglist', 'deleteCondition')

        result = response.data

        if (result.success) {

        } else {
          this.dispatch('Error', typeof result.error === 'string' ? result.error : result.status_message)
        }
      } catch (e) {
        this.dispatch('Error', e)
      }
      return result
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/Payroll.js